import type { Step } from '@/components/Stepper/StepItem';
import type { CycleInstance } from '@/types/cycle-evaluation/CycleInstance';
import type { IconName } from '@amelio/ui-library';

import { CycleInstanceSteps } from '@/constants/cycle-evaluation/CycleInstanceSteps';
import {
    CycleInstanceStatus,
    ReviewType,
    type ReviewTypeValue,
} from '@/constants/review/CycleInstance';
import {
    Feedback360SettingsLock,
    type Feedback360SettingsLockValue,
} from '@/constants/review/Feedback360Settings';
import { isAnonymousFeedback } from '@/constants/review/FeedbackPrivacy';
import { ReviewInstanceStatus } from '@/constants/review/ReviewInstanceStatus';
import i18n from '@/plugins/i18n';
import { CycleEvaluationGroupStatus } from '@/types/cycle-evaluation/CycleEvaluationGroup';

interface GetInitialMaxStepParams {
    cycleInstance?: CycleInstance;
    steps: Step[];
}

export const hasOnlyOneReviewee = (cycleInstance: CycleInstance) => {
    return cycleInstance.reviewAudience?.reviewAudienceAssignments.length === 1;
};

export const getReviewTypeLabel = (reviewType: ReviewTypeValue) => {
    const { t } = i18n;

    switch (reviewType) {
        case ReviewType.EVALUATION_360:
            return t('common.reviewType.evaluation360');
        case ReviewType.MANAGER_EVALUATION:
            return t('common.reviewType.managerEvaluation');
        case ReviewType.SELF_EVALUATION:
            return t('common.reviewType.selfEvaluation');
        default:
            return t('common.reviewType.evaluation360');
    }
};

export const isSelfEvaluationReviewType = (reviewType?: ReviewTypeValue): boolean => {
    return reviewType === ReviewType.SELF_EVALUATION;
};

/**
 * Only a 360 gathers answers from respondents: a self or manager assessment has a single author.
 * A missing review type reads as a 360, like its label and icon.
 */
export const hasRespondents = (reviewType?: ReviewTypeValue): boolean => {
    return (reviewType ?? ReviewType.EVALUATION_360) === ReviewType.EVALUATION_360;
};

export const getReviewTypeIcon = (reviewType: ReviewTypeValue): IconName => {
    switch (reviewType) {
        case ReviewType.SELF_EVALUATION:
            return 'users-profile-tick';
        case ReviewType.MANAGER_EVALUATION:
            return 'arrow-refresh-2';
        case ReviewType.EVALUATION_360:
        default:
            return 'arrow-rotate-right';
    }
};

export const getAllCycleInstanceAssignedUserIds = (
    cycleInstances: CycleInstance[],
): Set<string> => {
    const userIds = new Set<string>();

    cycleInstances.forEach((cycleInstance) => {
        if (cycleInstance.assignedTo) {
            userIds.add(cycleInstance.assignedTo);
        }

        if (cycleInstance.reviewAudience?.reviewAudienceAssignments) {
            cycleInstance.reviewAudience.reviewAudienceAssignments.forEach((assignment) => {
                if (assignment.assignedTo) {
                    userIds.add(assignment.assignedTo);
                }

                if (assignment.reviewEvaluatorAssignments) {
                    assignment.reviewEvaluatorAssignments.forEach((evaluatorAssignment) => {
                        if (evaluatorAssignment.assignedTo) {
                            userIds.add(evaluatorAssignment.assignedTo);
                        }
                    });
                }
            });
        }

        if (cycleInstance.reviewEvaluatorSelection?.reviewEvaluatorAssignments) {
            cycleInstance.reviewEvaluatorSelection.reviewEvaluatorAssignments.forEach(
                (evaluatorAssignment: { assignedTo?: string }) => {
                    if (evaluatorAssignment.assignedTo) {
                        userIds.add(evaluatorAssignment.assignedTo);
                    }
                },
            );
        }
    });

    return userIds;
};

export const getStepPath = (step: string) => {
    switch (step) {
        case CycleInstanceSteps.SELECT_REVIEWEE:
            return 'select-reviewee';
        case CycleInstanceSteps.ASSIGN_REVIEWER:
            return 'assign-reviewer';
        case CycleInstanceSteps.SELECT_TEMPLATE:
            return 'select-template';
        case CycleInstanceSteps.QUESTIONNAIRE_CREATION:
            return 'questionnaire-creation';
        case CycleInstanceSteps.SCHEDULE_CREATION:
            return 'schedule-creation';
    }
};

export const getStepFromPath = (path: string): string | undefined => {
    switch (path) {
        case 'select-reviewee':
            return CycleInstanceSteps.SELECT_REVIEWEE;
        case 'assign-reviewer':
            return CycleInstanceSteps.ASSIGN_REVIEWER;
        case 'select-template':
            return CycleInstanceSteps.SELECT_TEMPLATE;
        case 'questionnaire-creation':
            return CycleInstanceSteps.QUESTIONNAIRE_CREATION;
        case 'schedule-creation':
            return CycleInstanceSteps.SCHEDULE_CREATION;
    }
};

// Excludes COMPLETED: Past Reviews don't expose an Edit menu, so routing never dispatches on them.
export const isOngoingCycleInstance = (cycleInstance: CycleInstance): boolean => {
    return (
        cycleInstance.status === CycleInstanceStatus.ACTIVE ||
        cycleInstance.status === CycleInstanceStatus.LAUNCHED
    );
};

/**
 * A 360 generated by a cycle takes the cycle's settings; one created on its own keeps its confidentiality from
 * launch on, and both settings once it is over.
 */
export const getFeedback360SettingsLock = (
    cycleInstance?: CycleInstance,
): Feedback360SettingsLockValue | undefined => {
    if (!cycleInstance) {
        return undefined;
    }
    if (cycleInstance.cycleEvaluationId) {
        return Feedback360SettingsLock.INHERITED;
    }
    if (cycleInstance.commentRepliesLocked) {
        return Feedback360SettingsLock.COMPLETED;
    }
    return cycleInstance.status === CycleInstanceStatus.DRAFT
        ? undefined
        : Feedback360SettingsLock.LAUNCHED;
};

/**
 * Whether the person evaluated launched this 360 themselves, and so reads its results. Only for a preview: on a
 * respondent's link, the server's answer decides.
 */
export const isLaunchedByReviewee = (cycleInstance?: CycleInstance, revieweeId?: string): boolean =>
    cycleInstance?.reviewType === ReviewType.EVALUATION_360 &&
    !!revieweeId &&
    cycleInstance.assignedTo === revieweeId;

/** A launched anonymous 360 keeps every respondent: removing one could tell who answered. */
export const isReviewerRemovalLocked = (cycleInstance?: CycleInstance): boolean =>
    !!cycleInstance &&
    isAnonymousFeedback(cycleInstance.feedbackPrivacy) &&
    cycleInstance.status !== CycleInstanceStatus.DRAFT;

/**
 * A 360 that is over takes no new respondent: the server refuses them. Split into clones, it has no review of its own
 * and the server says when all are over. With reviews, it goes on while one is open: the reviews read here do not say
 * whose they are, so the server alone refuses an addition for a person whose own review has ended.
 */
export const isReviewerAdditionLocked = (cycleInstance?: CycleInstance): boolean => {
    if (!cycleInstance?.reviewInstances?.length) {
        return !!cycleInstance?.commentRepliesLocked;
    }

    return (
        cycleInstance.status === CycleInstanceStatus.COMPLETED ||
        cycleInstance.reviewInstances.every(({ status }) => status !== ReviewInstanceStatus.ACTIVE)
    );
};

export const canCycleInstanceSendEmail = (cycleInstance?: CycleInstance): boolean => {
    const groupStatus = cycleInstance?.groupStatus;

    return (
        groupStatus === undefined ||
        groupStatus === null ||
        groupStatus === CycleEvaluationGroupStatus.ACTIVE
    );
};

export const getInitialMaxStep = ({
    cycleInstance,
    steps,
}: GetInitialMaxStepParams): number | undefined => {
    if (!cycleInstance) {
        return undefined;
    }

    if (isOngoingCycleInstance(cycleInstance)) {
        return steps.length - 1;
    }

    const firstIncompleteIndex = steps.findIndex((s) => s.id === cycleInstance.firstIncompleteStep);

    return firstIncompleteIndex >= 0 ? firstIncompleteIndex : undefined;
};

const TEMPLATE_STEP_BYPASSED_STEPS: CycleInstanceSteps[] = [
    CycleInstanceSteps.QUESTIONNAIRE_CREATION,
    CycleInstanceSteps.SCHEDULE_CREATION,
];

export const isTemplateStepCompleted = (cycleInstance?: CycleInstance): boolean => {
    if (!cycleInstance) {
        return false;
    }

    return (
        !!cycleInstance.reviewQuestionnaire?.reviewTemplateId ||
        (cycleInstance.firstIncompleteStep != null &&
            TEMPLATE_STEP_BYPASSED_STEPS.includes(cycleInstance.firstIncompleteStep))
    );
};

export const getPrepareLaunchLandingStep = (cycleInstance: CycleInstance): CycleInstanceSteps => {
    if (isSelfEvaluationReviewType(cycleInstance.reviewType)) {
        return cycleInstance.firstIncompleteStep === CycleInstanceSteps.SCHEDULE_CREATION
            ? CycleInstanceSteps.QUESTIONNAIRE_CREATION
            : (cycleInstance.firstIncompleteStep ?? CycleInstanceSteps.QUESTIONNAIRE_CREATION);
    }

    if (cycleInstance.firstIncompleteStep === CycleInstanceSteps.SELECT_REVIEWEE) {
        return CycleInstanceSteps.SELECT_REVIEWEE;
    }

    return CycleInstanceSteps.ASSIGN_REVIEWER;
};

export const getLastStepForReviewType = (reviewType: ReviewTypeValue): CycleInstanceSteps => {
    switch (reviewType) {
        case ReviewType.EVALUATION_360:
        case ReviewType.SELF_EVALUATION:
        case ReviewType.MANAGER_EVALUATION:
            return CycleInstanceSteps.SCHEDULE_CREATION;
    }
};

export interface CycleInstanceEvaluationPeriod {
    start: string;
    end: string;
}

/**
 * The evaluation period a cycle instance carries: its own when it has one, otherwise the period
 * configured on its cycle group (group-launched self assessments and 360s only carry the group's).
 * The backend writes both bounds together, so a lone bound counts as no period.
 */
export const getCycleInstanceEvaluationPeriod = (
    cycleInstance?: CycleInstance,
): CycleInstanceEvaluationPeriod | undefined => {
    if (!cycleInstance) {
        return undefined;
    }

    const { evaluationPeriodStart, evaluationPeriodEnd } = cycleInstance;
    if (evaluationPeriodStart && evaluationPeriodEnd) {
        return { start: evaluationPeriodStart, end: evaluationPeriodEnd };
    }

    const { groupEvaluationPeriodStart, groupEvaluationPeriodEnd } = cycleInstance;
    if (groupEvaluationPeriodStart && groupEvaluationPeriodEnd) {
        return { start: groupEvaluationPeriodStart, end: groupEvaluationPeriodEnd };
    }

    return undefined;
};
